<script setup lang="ts">
    import { computed, watch, nextTick, toRef } from 'vue'
    import type { PropType } from 'vue'
    import type { CriterionSelectorDialogViewModel } from "@Criteria/Hierarchies/Components/CriterionSelectorDialog"

    const $ = ((window as any).kendo?.jQuery ?? (window as any).$) as JQueryStatic

    // ---------------- props / emits ----------------

    const props = defineProps({
        viewModel: {
            type: Object as PropType<CriterionSelectorDialogViewModel>,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'confirm-selection', id: number): void
    }>()

    // ---------------- computed ----------------

    const vm = toRef(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    // ---------------- types ----------------

    type CriterionNode = {
        Id: number
        ParentId: number | null
        Name: string
        Subcriteria: CriterionNode[]
    }

    // ---------------- helpers ----------------

    function buildTree(criteria: any[]): CriterionNode | undefined
    {
        const map = new Map<number, CriterionNode>()
        let root: CriterionNode | undefined

        // create nodes
        for (const c of criteria)
        {
            map.set(c.Id, {
                Id: c.Id,
                ParentId: c.ParentId,
                Name: c.Name,
                Subcriteria: []
            })
        }

        // wire hierarchy
        for (const node of map.values())
        {
            if (node.ParentId == null)
            {
                root = node
            } else
            {
                map.get(node.ParentId)?.Subcriteria.push(node)
            }
        }

        return root
    }

    // ---------------- watcher ----------------

    watch(opened, async (val) =>
    {
        if (!val) return

        const goal = buildTree(vm.value.criteria)
        if (!goal) return

        await nextTick()

        $("#treeview").kendoTreeView({
            dataSource: new kendo.data.HierarchicalDataSource({
                data: [goal],
                schema: {
                    model: {
                        id: "Id",
                        children: "Subcriteria"
                    }
                }
            }),
            dataTextField: "Name",
            loadOnDemand: false
        })

        const treeview = $("#treeview").data("kendoTreeView")

        const selected = treeview.dataSource.get(vm.value.selectedCriterionId)

        // expand path to selected first (important)
        if (selected) {
            treeview.expandTo(selected)
        }

        // expand first visible level
        treeview.dataSource.view().forEach((node: any) =>
        {
            treeview.expand(treeview.findByUid(node.uid))
        })

        // finally select
        if (selected) {
            treeview.select(treeview.findByUid(selected.uid))
        }

    })

    // ---------------- actions ----------------

    function confirm()
    {
        const tv = $("#treeview").data("kendoTreeView")
        const selectedNode = tv.select()
        const item = tv.dataItem(selectedNode)

        emit('confirm-selection', item.id)
        close()
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Select a criterion</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div id="treeview"></div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">
                            Close
                        </button>
                        <button type="button" @click="confirm" class="btn btn-success">
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
